import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDocumentMeta } from '../lib/use-document-meta.js';
import { caseStudyMeta } from '../lib/route-meta.js';
import CaseStudySchema from '../components/CaseStudySchema.jsx';
import './CaseStudies.css';
import './CaseStudyDetail.css';

// Public detail page for a single case study. Reads pre-rendered HTML
// from /api/get-case-study so the marketing bundle stays slim.
export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [state, setState] = useState({ status: 'loading', data: null, error: null });

  // Title, description and canonical come from route-meta.js, which
  // middleware.js also reads, so the served HTML and the hydrated document
  // agree. Falls back to a generic case study page while loading.
  const d = state.data;
  useDocumentMeta(caseStudyMeta(d, slug));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/get-case-study?slug=${encodeURIComponent(slug)}`);
        if (cancelled) return;
        if (res.status === 404) {
          setState({ status: 'not_found', data: null, error: null });
          return;
        }
        if (!res.ok) {
          setState({ status: 'error', data: null, error: `Could not load (${res.status})` });
          return;
        }
        const data = await res.json();
        setState({ status: 'ready', data, error: null });
      } catch (err) {
        if (cancelled) return;
        setState({ status: 'error', data: null, error: err.message || 'Network error' });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (state.status === 'loading') return <Shell><div className="cs-state">Loading…</div></Shell>;
  if (state.status === 'not_found') {
    return (
      <Shell>
        <div className="cs-state">
          <h1 className="csd-not-found-title">Case study not found.</h1>
          <p>It may have been moved, or you might be following an old link.</p>
          <p><Link to="/case-studies" className="cs-link">← All case studies</Link></p>
        </div>
      </Shell>
    );
  }
  if (state.status === 'error') {
    return (
      <Shell>
        <div className="cs-state">Couldn't load. {state.error}</div>
      </Shell>
    );
  }

  return (
    <Shell>
      <CaseStudySchema caseStudy={d} slug={slug} />
      <article className="csd-article">
        <div className="csd-meta">
          <Link to="/case-studies" className="csd-meta-link">← Case studies</Link>
        </div>

        <header className="csd-hero">
          {d.hero_kicker && <div className="csd-hero-kicker">{d.hero_kicker}</div>}
          <h1 className="csd-hero-name">{d.display_name}</h1>
          <div className="csd-hero-business">
            {d.display_business}
            {d.display_city && <span className="csd-hero-sep"> · {d.display_city}</span>}
          </div>
          {d.hero_quote && (
            <blockquote className="csd-hero-quote">
              <p>{d.hero_quote}</p>
              {d.hero_quote_attrib && <cite>{d.hero_quote_attrib}</cite>}
            </blockquote>
          )}
        </header>

        {d.intro_html && <Section html={d.intro_html} className="csd-intro" />}

        {d.threads_html && (
          <Section
            html={d.threads_html}
            className="csd-threads"
            heading="What I saw"
          />
        )}

        {d.growth_math_html && (
          <Section
            html={d.growth_math_html}
            className="csd-growth-math"
            heading="The math"
          />
        )}

        {d.plan_html && (
          <Section
            html={d.plan_html}
            className="csd-plan"
            heading="What I built"
          />
        )}

        {d.outcomes_html && (
          <Section
            html={d.outcomes_html}
            className="csd-outcomes"
            heading={d.active ? 'Where it stands' : "What's happened since I left"}
            dateline={buildDateline(d)}
          />
        )}

        {d.closing_html && <Section html={d.closing_html} className="csd-closing" />}

        <footer className="csd-footer">
          <p className="csd-footer-cta">
            If this sounds like something on your desk, <Link to="/#offer" className="cs-link">tell me about it</Link>.
          </p>
        </footer>
      </article>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="cs-page">
      <header className="cs-header">
        <Link to="/" className="cs-brand">margin</Link>
        <Link to="/#offer" className="cs-cta">Start a conversation</Link>
      </header>
      <main className="cs-main">{children}</main>
    </div>
  );
}

function Section({ html, className, heading, dateline }) {
  return (
    <section className={`csd-section ${className || ''}`}>
      {heading && <h2 className="csd-section-heading">{heading}</h2>}
      {dateline && <p className="csd-dateline">{dateline}</p>}
      <div className="csd-prose" dangerouslySetInnerHTML={{ __html: html }} />
    </section>
  );
}

// "Handed off 2024 · last verified August 2026". Either half may be missing.
function buildDateline({ status_note: note, last_verified: verified }) {
  const when = formatMonthYear(verified);
  if (note && when) return `${note} · last verified ${when}`;
  if (note) return note;
  if (when) return `Last verified ${when}`;
  return null;
}

// Parsed off the string rather than through Date, because new Date('2026-08-01')
// is UTC midnight and renders as July in any negative offset.
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function formatMonthYear(isoDate) {
  if (!isoDate) return null;
  const m = /^(\d{4})-(\d{2})/.exec(isoDate);
  if (!m) return null;
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${month} ${m[1]}` : null;
}
